import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Meta from '../components/Meta';

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Lightbox State
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    setLoading(true);
    setError(false);
    
    // Fetch service by slug
    fetch(`https://api.dev.auxo.dk/public/by-slug/da/ydelse-${slug}`)
      .then(res => {
        if (!res.ok) throw new Error('Service ikke fundet');
        return res.json();
      })
      .then(serviceData => {
        setService(serviceData);
        
        // Fetch gallery by gallery_slug
        if (serviceData.gallery_slug) {
          return fetch(`https://api.dev.auxo.dk/public/by-slug/da/${serviceData.gallery_slug}`)
            .then(res => res.ok ? res.json() : null)
            .then(galleryData => {
              setGallery(galleryData);
            });
        }
      })
      .then(() => {
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError(true);
        setLoading(false);
      });
  }, [slug]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeImageIndex === null || !gallery?.images) return;
      if (e.key === 'Escape') setActiveImageIndex(null);
      if (e.key === 'ArrowRight') handleNextImage();
      if (e.key === 'ArrowLeft') handlePrevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeImageIndex, gallery]);

  // Helper to format content list into a table
  const getFormattedContent = () => {
    const content = service?.content?.da;
    if (!content) return '';

    if (slug === 'fastpris' && content.includes('Systempris (grundlast)')) {
      const tableHtml = `
        <div class="overflow-x-auto my-8 border border-slate-200 rounded-sm shadow-sm">
          <table class="min-w-full divide-y divide-slate-200">
            <thead class="bg-slate-50">
              <tr>
                <th scope="col" class="px-6 py-4 text-left text-xs font-bold text-[#253872] uppercase tracking-wider border-b border-slate-200">Komponent</th>
                <th scope="col" class="px-6 py-4 text-left text-xs font-bold text-[#253872] uppercase tracking-wider border-b border-slate-200">Beskrivelse</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-slate-100">
              <tr class="hover:bg-slate-50/50 transition-colors">
                <td class="px-6 py-4 text-sm font-bold text-[#253872]">Systempris (grundlast)</td>
                <td class="px-6 py-4 text-sm text-slate-600">Fastlægges på Nordpool</td>
              </tr>
              <tr class="hover:bg-slate-50/50 transition-colors">
                <td class="px-6 py-4 text-sm font-bold text-[#253872]">CFD -/+</td>
                <td class="px-6 py-4 text-sm text-slate-600">Prisdifferencer på Nordpool</td>
              </tr>
              <tr class="hover:bg-slate-50/50 transition-colors">
                <td class="px-6 py-4 text-sm font-bold text-[#253872]">Balance</td>
                <td class="px-6 py-4 text-sm text-slate-600">Leverandørens omkostning til balancehåndtering</td>
              </tr>
              <tr class="hover:bg-slate-50/50 transition-colors">
                <td class="px-6 py-4 text-sm font-bold text-[#253872]">Profil -/+</td>
                <td class="px-6 py-4 text-sm text-slate-600">Forbrugsprofilering hos leverandøren</td>
              </tr>
              <tr class="hover:bg-slate-50/50 transition-colors">
                <td class="px-6 py-4 text-sm font-bold text-[#253872]">Valuta (euro til d.kr.)</td>
                <td class="px-6 py-4 text-sm text-slate-600">Valutakursregulering hos leverandøren</td>
              </tr>
              <tr class="hover:bg-slate-50/50 transition-colors">
                <td class="px-6 py-4 text-sm font-bold text-[#253872]">Kredit omk.</td>
                <td class="px-6 py-4 text-sm text-slate-600">Leverandørens kreditomkostninger</td>
              </tr>
              <tr class="hover:bg-slate-50/50 transition-colors">
                <td class="px-6 py-4 text-sm font-bold text-[#253872]">DB</td>
                <td class="px-6 py-4 text-sm text-slate-600">Leverandørens dækningsbidrag</td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
      return content.replace(/<ul>[\s\S]*?Systempris[\s\S]*?<\/ul>/, tableHtml);
    }
    return content;
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-white">
        <div className="text-center space-y-4">
          <i className="fa-regular fa-spinner-third animate-spin text-4xl text-brand-orange" />
          <p className="text-brand-muted text-xs uppercase tracking-wider font-bold">Henter indhold...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-light px-6">
        <div className="text-center max-w-md space-y-6 bg-white p-10 rounded-sm border border-slate-100 shadow-sm">
          <div className="text-brand-orange text-5xl">
            <i className="fa-regular fa-circle-exclamation" />
          </div>
          <h1 className="text-xl font-black text-slate-800 tracking-tight">Ydelsen blev ikke fundet</h1>
          <p className="text-brand-muted text-xs leading-relaxed">
            Den søgte ydelse findes desværre ikke i vores system, eller er blevet flyttet.
          </p>
          <Link 
            to="/" 
            className="inline-block bg-brand-orange hover:bg-brand-orange-hover text-white px-6 py-3 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors duration-200"
          >
            Tilbage til forsiden
          </Link>
        </div>
      </div>
    );
  }

  const handlePrevImage = () => {
    if (!gallery?.images) return;
    setActiveImageIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!gallery?.images) return;
    setActiveImageIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1));
  };

  // Touch swipe handling for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.screenX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.screenX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // minimum distance to trigger swipe
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        handleNextImage(); // Swiped left -> next
      } else {
        handlePrevImage(); // Swiped right -> prev
      }
    }
  };

  return (
    <>
      <Meta 
        title={service.title?.da} 
        description={service.description?.da}
      />

      {/* Header Section */}
      <section className="bg-brand-dark text-white py-24 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/57398cfb-6e5d-4ba8-a3b9-c24b8dc48392/hero-danish-houses.jpg" 
            alt="Moderne dansk boligområde" 
            className="w-full h-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-slate-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="space-y-4 max-w-2xl text-left">
            <Link to="/" className="inline-flex items-center gap-2 text-white hover:text-slate-200 text-xs font-bold uppercase tracking-wider hover:underline mb-2">
              <i className="fa-regular fa-arrow-left text-[10px]" /> Forside
            </Link>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
              {service.title?.da}
            </h1>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
              {service.description?.da}
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-8 text-left">
          {/* Rich Text Content */}
          <div className="space-y-8">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight leading-tight">
              Dybdegående information om <span className="text-brand-orange">{service.title?.da.toLowerCase()}</span>
            </h2>
            
            <div 
              className="prose text-brand-muted leading-relaxed space-y-6"
              dangerouslySetInnerHTML={{ __html: getFormattedContent() }}
            />
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="bg-brand-light py-20 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight leading-tight">
            Skal vi hjælpe dig med <span className="text-brand-orange">{service.title?.da.toLowerCase()}?</span>
          </h2>
          <p className="text-brand-muted text-sm leading-relaxed max-w-xl mx-auto">
            Kontakt os i dag for at få et uforpligtende tilbud eller høre mere om, hvordan vi kan hjælpe dig med din ejendom.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/kontakt" 
              className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-3.5 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors duration-200"
            >
              Bestil her
            </Link>
            <Link 
              to="/kontakt" 
              className="bg-white hover:bg-slate-50 text-brand-dark border border-slate-200 px-8 py-3.5 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors duration-200"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
